/**
 * Super-admin uploader for the three configurable brand logos:
 *   • app   — used everywhere by default (header, sidebar, footer, drawer)
 *   • shop  — /shop marketplace
 *   • score — /score public rating explorer
 *
 * Empty slot → the app falls back to /brand/socibaba-icon.png.
 * Images are stored as base64 data URLs in Mongo (5 MB cap per slot).
 */
import React, { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Upload, Loader2, Trash2, Image as ImageIcon } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

type Slot = "app" | "shop" | "score";
interface SlotSpec { key: Slot; label: string; hint: string; }
const SLOTS: SlotSpec[] = [
  { key: "app", label: "Main app logo", hint: "Shown on header, footer, sidebar and mobile drawer." },
  { key: "shop", label: "Shop logo", hint: "Shown on /shop marketplace." },
  { key: "score", label: "SIBA Score logo", hint: "Shown on the public /score rating explorer." },
];

const MAX_BYTES = 5 * 1024 * 1024;

async function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ""));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

export const BrandLogosCard: React.FC = () => {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const readonly = (me?.role || "user") !== "admin";

  const logos = useQuery<Record<Slot, string | null>>({
    queryKey: ["admin-brand-logos"],
    queryFn: async () => (await api.get("/admin/brand-logos")).data,
  });

  return (
    <div className="sg-card p-6" data-testid="brand-logos-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 rounded-xl bg-amber-50 dark:bg-amber-950/40 flex items-center justify-center shrink-0">
          <ImageIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
        </div>
        <div>
          <div className="font-display text-lg font-bold text-[color:var(--sg-fg)]">Brand logos</div>
          <div className="text-xs text-[color:var(--sg-muted)] mt-0.5">
            Upload PNG or SVG icons per surface. Max 5&nbsp;MB each. The brand name
            next to the icon always stays plain text — never uploaded as an image.
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {SLOTS.map((s) => (
          <SlotUploader
            key={s.key}
            spec={s}
            value={logos.data?.[s.key] || null}
            readonly={readonly}
            onSaved={() => qc.invalidateQueries({ queryKey: ["admin-brand-logos"] })}
          />
        ))}
      </div>
    </div>
  );
};

const SlotUploader: React.FC<{
  spec: SlotSpec;
  value: string | null;
  readonly: boolean;
  onSaved: () => void;
}> = ({ spec, value, readonly, onSaved }) => {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();

  const save = useMutation({
    mutationFn: async (data_url: string | null) =>
      (await api.put(`/admin/brand-logos/${spec.key}`, { data_url })).data,
    onSuccess: () => {
      toast.success(`${spec.label} saved`);
      onSaved();
      // Public consumers also cache via useBrandLogos — bust that too.
      qc.invalidateQueries({ queryKey: ["brand-logos"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const onFile = async (f: File | undefined) => {
    if (!f) return;
    if (f.size > MAX_BYTES) {
      toast.error("Image is larger than 5 MB");
      return;
    }
    if (!f.type.startsWith("image/")) {
      toast.error("Only image files are allowed");
      return;
    }
    setBusy(true);
    try {
      const dataUrl = await fileToDataUrl(f);
      await save.mutateAsync(dataUrl);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div
      data-testid={`brand-logo-slot-${spec.key}`}
      className="rounded-xl border border-[color:var(--sg-border)] bg-[color:var(--sg-muted-bg)]/40 p-4 flex flex-col gap-3"
    >
      <div>
        <div className="text-sm font-semibold text-[color:var(--sg-fg)]">{spec.label}</div>
        <div className="text-[11px] text-[color:var(--sg-muted)] mt-0.5 leading-snug">{spec.hint}</div>
      </div>
      <div className="flex items-center justify-center h-24 rounded-lg bg-white dark:bg-[color:var(--sg-card)] border border-dashed border-[color:var(--sg-border)]">
        {value ? (
          <img
            src={value}
            alt={`${spec.label} preview`}
            loading="lazy"
            decoding="async"
            className="max-h-20 max-w-full object-contain"
            data-testid={`brand-logo-preview-${spec.key}`}
          />
        ) : (
          <div className="text-[11px] text-[color:var(--sg-muted)]">Using default</div>
        )}
      </div>
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          className="hidden"
          data-testid={`brand-logo-input-${spec.key}`}
          onChange={(e) => onFile(e.target.files?.[0])}
          disabled={readonly || busy}
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={readonly || busy}
          onClick={() => inputRef.current?.click()}
          data-testid={`brand-logo-upload-${spec.key}`}
          className="flex-1 h-8"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1" />}
          {value ? "Replace" : "Upload"}
        </Button>
        {value && !readonly && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => save.mutate(null)}
            data-testid={`brand-logo-clear-${spec.key}`}
            className="h-8 border-red-200 text-red-700 hover:bg-red-50"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default BrandLogosCard;
